import {
	Component,
	EventEmitter,
	HostListener,
	Input,
	OnChanges,
	OnDestroy,
	OnInit,
	Output,
	SimpleChanges,
	ViewChild
} from '@angular/core';
import { AccountServiceWebProvider } from '@web-services/user/account.service';
import { PropsAction, User } from '@models/models';
import {
	BrandCreationData,
	BrandDetailsInfo,
	Brands,
	BrandsDetails,
	BrandsListItem
} from '@models/brands.model';
import { NewSubscriptionServiceDataProvider } from '@services/subscription/subscription.service';
import { finalize, switchMap, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs/Subject';
import { BrandsDataServiceProvider } from '@services/brands/brands.data.service';
import { SNModalService } from '@web-services/modal/snModal.service';
import { BrandCreateModalComponent } from '@web-components/brands/modals/brand-create-modal/brand-create-modal';
import { EmptyError } from 'rxjs';
import { BrandsServiceProvider } from '@services/brands/brands.service';
import { Location } from '@angular/common';
import { ImportDataModalComponent } from '@web-components/brands/modals/import-data-modal/import-data-modal';
import { NgbModalRef } from '@ng-bootstrap/ng-bootstrap';

@Component({
	selector: 'user-menu',
	template: require('./user-menu.html'),
	styles: [require('./user-menu.scss')]
})
export class UserMenuComponent implements OnInit, OnChanges, OnDestroy {
	@Input() user: User;
	@Input() brands: Brands;
	@Output() emitSwitchBrand = new EventEmitter();
	@ViewChild('mobileMenu') mobileMenu;

	// shows brand routes list from the start
	isRoutesMenuOpen = true;
	isMobileMenuOpen = false;
	activeBrand: BrandsListItem;
	importModalRef: NgbModalRef;
	actionList: Array<string> = [];
	private unsubscribe: Subject<void> = new Subject();

	constructor(
		private accountService: AccountServiceWebProvider,
		private subscriptionService: NewSubscriptionServiceDataProvider,
		private brandsDataService: BrandsDataServiceProvider,
		private brandsService: BrandsServiceProvider,
		private snModalService: SNModalService,
		private location: Location
	) {}

	@HostListener('window:resize')
	onWindowResize(): void {
		this.isMobileMenuOpen = false;
	}

	ngOnInit(): void {
		this.subscribeToBrandsChange();
	}

	ngOnChanges(change: SimpleChanges): void {
		if (change.brands && change.brands.currentValue) {
			this.actionList = this.brandsService.createActionList(
				change.brands.currentValue.actions
			);
			this.setActiveBrand(change.brands.currentValue);
		}
	}

	ngOnDestroy(): void {
		this.unsubscribe.next();
		this.unsubscribe.complete();
	}

	onMenuItemClick(selected: boolean, brand: BrandsListItem): void {
		selected ? this.toggleRoutes() : this.activateBrand(brand);
	}

	logout(): void {
		this.accountService.logoutWeb();
	}

	toggleMobileNav(): void {
		this.isRoutesMenuOpen = true;
		this.isMobileMenuOpen = !this.isMobileMenuOpen;
		this.scrollUserMenuToTop();
	}

	toggleRoutes(): void {
		this.isRoutesMenuOpen = !this.isRoutesMenuOpen;
	}

	createNewBrand(): void {
		this.isMobileMenuOpen = false;
		this.isRoutesMenuOpen = true;

		if (this.brands.actions.UPGRADE_PLAN) {
			this.subscriptionService.setChoosePlanOpenModalStatus({
				state: true,
				payload: { url: this.brands.actions.UPGRADE_PLAN.url }
			});
		}

		if (this.brands.actions.ADD_NEW_BRAND) {
			this.snModalService
				.openModal(BrandCreateModalComponent)
				.result.then(
					(
						brandData: PropsAction<BrandCreationData | BrandDetailsInfo>
					): void => this.onCreateModalClosed(brandData)
				)
				.catch(() => EmptyError);
		}
	}

	closeMobileNav() {
		this.isMobileMenuOpen = false;
	}

	getClass(path) {
		let locationPath = location.pathname;

		if (locationPath.includes('/invoices')) {
			locationPath = '/payments';
		}

		if (path === '/') {
			if (
				locationPath.substring(
					0,
					location.protocol.length + location.host.length
				) === path
			) {
				return 'selected-menu';
			}
			return 'not-selected-menu';
		}
		if (locationPath.includes(path)) {
			return 'selected-menu';
		}
		return 'not-selected-menu';
	}

	activateBrand(brand: BrandsListItem): void {
		if (!brand.selected) {
			this.isMobileMenuOpen = false;
			this.isRoutesMenuOpen = true;
			this.emitSwitchBrand.emit(true);

			this.brandsDataService
				.switchBrands(brand.companyId)
				.pipe(
					takeUntil(this.unsubscribe),
					switchMap(() =>
						this.accountService
							.updateSystemDueToBrandChange()
							.pipe(finalize((): void => this.emitSwitchBrand.emit(false)))
					)
				)
				.subscribe(() => {
					this.brandsDataService.setBrandsChange(location.pathname === '/');
				});
		}
	}

	private scrollUserMenuToTop(): void {
		if (this.isMobileMenuOpen) {
			setTimeout((): void => {
				this.mobileMenu.nativeElement.scrollBy(
					0,
					-this.mobileMenu.nativeElement.scrollTop
				);
			}, 0);
		}
	}

	private subscribeToBrandsChange(): void {
		this.brandsDataService.brandsChange
			.pipe(takeUntil(this.unsubscribe))
			.subscribe((): void => this.location.go('/'));
	}

	private onCreateModalClosed(
		brandData: PropsAction<BrandCreationData | BrandDetailsInfo>
	): void {
		if (brandData.action === 'IMPORT') {
			this.handleDataImportModal(brandData.props);
		}

		if (brandData.action === 'REDIRECT') {
			this.updateAndRedirect(brandData.props.companyId);
		}
	}

	private handleDataImportModal(brandData: BrandCreationData): void {
		this.importModalRef = this.snModalService.openModal(
			ImportDataModalComponent
		);

		this.importModalRef.componentInstance.newBrandData = brandData;

		this.importModalRef.result
			.then((res: BrandDetailsInfo): void => {
				this.updateAndRedirect(res.companyId);
			})
			.catch();
	}

	private setActiveBrand(brands: Brands): void {
		brands.brands.forEach((e: BrandsListItem) => {
			if (e.companyId === brands.authCompanyId) {
				this.activeBrand = e;
			}
		});
	}

	private updateAndRedirect(companyId: number): void {
		this.location.go(`/settings/company/${companyId}/edit`);
	}
}



// WEBPACK FOOTER //
// ./node_modules/angular2-template-loader!./node_modules/string-replace-webpack-plugin/loader.js?id=oom9nsliule!./web/modern/components/navbar/user-navbar/user-menu/user-menu.ts